import { debounce } from "lodash-es";
import { watch } from "vue";

import { getSignals } from "@/backend/read/BackendReader";
import { useEventBus } from "@/composables/useEventBus";
import { useSyncedTask } from "@/composables/useSyncedTask";
import type { Link } from "@/model/link";

import { useBoardStore } from "../board";
import { useLinkStore } from "../link";
import type { GraphResults } from "./signals";
import { useSignalStore } from "./signals";

const REFRESH_DEBOUNCE_MS = 2000;
// a cold session answers "pending" while the server builds the graph
const PENDING_MAX_RETRIES = 5;
const PENDING_RETRY_DELAY_MS = 2000;

const { onStickyNoteAction } = useEventBus({ neverUnregister: true });

const { runSync } = useSyncedTask();

/**
 * Keeps graph signals fresh while one is selected in search: refetches them all
 * (debounced) after sticky changes on the current board and link changes touching it.
 * Changes on other boards are picked up on the next fetch.
 */
export function initSignalSync() {
  watch(
    () => [useBoardStore().board?.id, useSignalStore().graphSignalSelected] as const,
    ([boardId, selected]) => {
      // a revisited board may have changed meanwhile, so a stored result is refetched too
      if (boardId && selected) void fetchSignals();
    },
  );
  onStickyNoteAction(null, "changed", ({ id }) => {
    if (!useSignalStore().graphSignalSelected) return;
    if (useBoardStore().board?.cards[id]) refreshSoon();
  });
  useLinkStore().$onAction((action) => {
    if (!useSignalStore().graphSignalSelected) return;
    if (action.name === "addObjectiveLink" || action.name === "removeObjectiveLink") {
      if (useBoardStore().board?.cards[action.args[0]]) refreshSoon();
      return;
    }
    const link =
      action.name === "add"
        ? action.args[0]
        : action.name === "remove"
          ? useLinkStore().linksById[action.args[0]]
          : undefined;
    if (link && touchesCurrentBoard(link)) refreshSoon();
  });
}

/**
 * Fetches the current board's graph signals, if it has any. Only the latest request
 * writes results; an error clears them and is rethrown.
 */
export async function fetchSignals() {
  const board = useBoardStore().board;
  if (!board || !useSignalStore().signals.some((signal) => signal.source === "graph")) return;
  const store = useSignalStore();
  store.setLoading(board.id);
  try {
    await runSync(
      (isCurrent) => readSignals(board.id, isCurrent),
      (ids) => store.setResults({ boardId: board.id, ids }),
    );
  } catch (error) {
    store.setResults({ boardId: board.id, ids: null });
    throw error;
  }
}

// refetch after edits
const refreshSoon = debounce(() => {
  if (useSignalStore().graphSignalSelected) void fetchSignals();
}, REFRESH_DEBOUNCE_MS);

/**
 * The board's signal ids, null when not ready ("pending" is retried, then given up);
 * undefined once the request is no longer current.
 */
async function readSignals(
  boardId: string,
  isCurrent: () => boolean,
): Promise<GraphResults["ids"] | undefined> {
  for (let retry = 0; ; retry++) {
    const response = await getSignals(boardId);
    if (!isCurrent()) return undefined;
    if (response.state !== "pending" || retry >= PENDING_MAX_RETRIES) {
      return response.state === "ready"
        ? {
            unplannedDependencies: new Set(response.unplannedDependencies),
            noUpperLevelLink: new Set(response.noUpperLevelLink),
            noObjective: new Set(response.noObjective),
            notOnArtBoard: new Set(response.notOnArtBoard),
          }
        : null;
    }
    await new Promise((resolve) => setTimeout(resolve, PENDING_RETRY_DELAY_MS));
    if (!isCurrent()) return undefined;
  }
}

function touchesCurrentBoard(link: Link) {
  const board = useBoardStore().board;
  return !!board && [link.from, link.to].some((id) => useLinkStore().boardCardByLink(id, board));
}
